import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { jobCategories, contractTypes } from '@/mocks/jobs';

export interface JobRecord {
  id?: string;
  owner_id?: string;
  title: string;
  company: string;
  category: string;
  contract_type: string;
  salary_min: number | null;
  salary_max: number | null;
  city: string;
  description: string;
  requirements: string;
  contact_email: string;
  contact_phone: string;
}

const cities = ['Lomé', 'Sokodé', 'Kara', 'Atakpamé', 'Kpalimé', 'Tsévié', 'Dapaong', 'Autre'];

interface JobFormProps {
  ownerId: string;
  existing?: JobRecord | null;
  onSaved: () => void;
}

function emptyForm(): JobRecord {
  return {
    title: '',
    company: '',
    category: jobCategories[0],
    contract_type: contractTypes[0],
    salary_min: null,
    salary_max: null,
    city: 'Lomé',
    description: '',
    requirements: '',
    contact_email: '',
    contact_phone: '',
  };
}

function JobForm({ ownerId, existing, onSaved }: JobFormProps) {
  const [form, setForm] = useState<JobRecord>(() => (existing ? { ...existing } : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field: keyof JobRecord, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.company.trim()) {
      setStatus('error');
      setErrorMsg('Le titre du poste et le nom de l\'entreprise sont obligatoires.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      title: form.title.trim(),
      company: form.company.trim(),
      category: form.category,
      contract_type: form.contract_type,
      salary_min: form.salary_min !== null && form.salary_min > 0 ? form.salary_min : null,
      salary_max: form.salary_max !== null && form.salary_max > 0 ? form.salary_max : null,
      city: form.city.trim() || null,
      description: form.description.trim() || null,
      requirements: form.requirements.trim() || null,
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('jobs').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase.from('jobs').insert({ ...payload, owner_id: ownerId });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
            <i className="ri-briefcase-line text-secondary-400" />
          </span>
          Informations du poste
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Titre du poste *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Ex : Développeur Web Full-Stack"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Entreprise *</label>
            <input
              type="text"
              value={form.company}
              onChange={(e) => update('company', e.target.value)}
              placeholder="Nom de l'entreprise"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Secteur</label>
            <select
              value={form.category}
              onChange={(e) => update('category', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
            >
              {jobCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Type de contrat</label>
            <select
              value={form.contract_type}
              onChange={(e) => update('contract_type', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
            >
              {contractTypes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Salaire min (FCFA)</label>
            <input
              type="number"
              value={form.salary_min ?? ''}
              onChange={(e) => update('salary_min', e.target.value ? Number(e.target.value) : null)}
              placeholder="250000"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Salaire max (FCFA)</label>
            <input
              type="number"
              value={form.salary_max ?? ''}
              onChange={(e) => update('salary_max', e.target.value ? Number(e.target.value) : null)}
              placeholder="500000"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Ville</label>
            <select
              value={form.city}
              onChange={(e) => update('city', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
            >
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Description du poste</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Décrivez le poste, les missions et les responsabilités..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Profil recherché</label>
          <textarea
            value={form.requirements}
            onChange={(e) => update('requirements', e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="Compétences, diplômes, expérience requise..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400 resize-none"
          />
        </div>
      </section>

      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
            <i className="ri-contacts-line text-secondary-400" />
          </span>
          Contact pour les candidatures
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Email de contact</label>
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => update('contact_email', e.target.value)}
              placeholder="rh@entreprise.tg"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Téléphone</label>
            <input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => update('contact_phone', e.target.value)}
              placeholder="+228 90 00 00 00"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
            />
          </div>
        </div>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour' : 'Publier l\'offre'}
        </button>
      </div>
    </form>
  );
}

export default JobForm;